import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Gri() {
  const { t } = useLang();
  const { role } = useOrg();
  const [year, setYear] = useState(new Date().getFullYear());
  const [standards, setStandards] = useState([]);
  const [activeSeries, setActiveSeries] = useState("Universal");
  const [activeKey, setActiveKey] = useState("GRI 2");
  const [payload, setPayload] = useState(null);
  const [drafts, setDrafts] = useState({});

  const loadStandards = async () => {
    const { data } = await api.get("/gri/standards");
    setStandards(data);
  };

  const loadStandard = async (key, y) => {
    const encoded = key.replace(/\s+/g, "_");
    const { data } = await api.get(`/gri/${encoded}/${y}`);
    setPayload(data);
    setDrafts(data.disclosures || {});
  };

  useEffect(() => { loadStandards(); }, []);
  useEffect(() => { loadStandard(activeKey, year); }, [activeKey, year]);

  const grouped = useMemo(() => {
    const g = {};
    standards.forEach((s) => {
      if (!g[s.series]) g[s.series] = [];
      g[s.series].push(s);
    });
    return g;
  }, [standards]);

  const save = async () => {
    try {
      await api.put("/gri/disclosure", { standard: activeKey, year, disclosures: drafts });
      toast.success(t("esrsSaved"));
      loadStandard(activeKey, year);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="gri-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">GRI Standards · 2021</div>
          <h1 className="font-display text-3xl font-semibold mt-1 flex items-center gap-2">
            <Globe size={28} weight="duotone" className="text-primary" />
            {t("griTitle")}
          </h1>
          <p className="text-muted-foreground text-sm mt-2">{t("griDesc")}</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("year")}</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px] font-mono-data" data-testid="gri-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4].map((n) => {
                const y = new Date().getFullYear() - n;
                return <SelectItem key={`gri-y-${y}`} value={String(y)}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Series selector */}
      <div className="flex gap-2 border-b border-border overflow-x-auto">
        {Object.keys(grouped).map((series) => {
          const active = activeSeries === series;
          return (
            <button
              key={series}
              onClick={() => {
                setActiveSeries(series);
                if (grouped[series]?.[0]) setActiveKey(grouped[series][0].key);
              }}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${active ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`gri-series-${series.toLowerCase()}`}
            >
              {series} · {grouped[series]?.length || 0}
            </button>
          );
        })}
      </div>

      {/* Standards in this series */}
      <div className="flex gap-2 flex-wrap">
        {(grouped[activeSeries] || []).map((s) => {
          const active = activeKey === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setActiveKey(s.key)}
              className={`px-3 py-1.5 rounded-sm text-xs font-mono-data uppercase tracking-widest border transition-colors ${active ? "bg-primary/10 border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
              data-testid={`gri-std-${s.key.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {s.key} · {s.name}
            </button>
          );
        })}
      </div>

      {/* Datapoint editor */}
      {payload && payload.template && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <span className="text-primary font-mono-data">{payload.standard}</span>
              <span>·</span>
              <span>{payload.name}</span>
              <span className="text-xs uppercase font-mono-data text-muted-foreground ml-2">{payload.series}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {payload.template.length === 0 && (
              <div className="text-sm text-muted-foreground">{t("griNoDatapoints")}</div>
            )}
            {payload.template.map((dp) => (
              <div key={dp.code} className="space-y-1.5" data-testid={`gri-dp-${dp.code}`}>
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-xs font-mono-data uppercase text-primary bg-primary/10 px-2 py-0.5 rounded-sm">{payload.standard}: {dp.code}</span>
                  <span className="text-sm font-medium">{dp.label}</span>
                </div>
                {dp.hint && <div className="text-xs text-muted-foreground italic">{dp.hint}</div>}
                <Textarea
                  rows={3}
                  value={drafts[dp.code] || ""}
                  onChange={(e) => setDrafts({ ...drafts, [dp.code]: e.target.value })}
                  placeholder={t("esrsWriteDisclosure")}
                  disabled={role !== "owner" && role !== "editor"}
                  data-testid={`gri-input-${dp.code}`}
                />
              </div>
            ))}
            {(role === "owner" || role === "editor") && (
              <div className="flex justify-end">
                <Button onClick={save} data-testid="gri-save">{t("save")}</Button>
              </div>
            )}
            {payload.updated_at && (
              <div className="text-xs text-muted-foreground font-mono-data text-right">
                {t("esrsLastSaved")}: {payload.updated_at.slice(0, 19).replace("T", " ")}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
